import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from './Button'
import { IconButton } from './Button'
import { cn } from '@/lib/cn'

/**
 * Focus trapping, focus restore, and Escape handling live here once.
 * Drawer, Modal, and ConfirmDialog all go through it — do not hand-roll a
 * panel that bypasses this (docs/06-component-refactor.md §7.6).
 *
 * Hand-rolled rather than Radix on purpose: the overlay surface is three
 * components. If it grows much past this, swap Radix in behind the same API.
 */
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'

function useOverlayBehavior(open: boolean, onClose: () => void) {
  const panelRef = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    restoreTo.current = document.activeElement as HTMLElement | null

    // Move focus into the panel: its first control, or the panel itself when
    // it has none. Without this, keyboard focus stays behind the scrim.
    const panel = panelRef.current
    const firstControl = panel?.querySelector<HTMLElement>(FOCUSABLE)
    if (firstControl) firstControl.focus()
    else panel?.focus()

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return
      const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n) => n.offsetParent !== null,
      )
      if (nodes.length === 0) return
      const first = nodes[0]!
      const last = nodes[nodes.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = previousOverflow
      restoreTo.current?.focus?.()
    }
  }, [open, onClose])

  return panelRef
}

function Scrim({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/25"
      onClick={onClose}
      aria-hidden
    />
  )
}

/* ---------------------------------------------------------------- drawer */

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  footer,
  width = 'w-[560px]',
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: ReactNode
  footer?: ReactNode
  width?: string
  children: ReactNode
}) {
  const panelRef = useOverlayBehavior(open, onClose)
  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50">
      <Scrim onClose={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'absolute inset-y-0 right-0 flex max-w-full flex-col bg-surface shadow-drawer',
          'rounded-l-[10px]',
          width,
        )}
      >
        <header className="flex items-start gap-3 border-b border-rule px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[18px] leading-7">{title}</h2>
            {subtitle && <div className="mt-1 text-[13px] leading-5">{subtitle}</div>}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <X size={16} aria-hidden />
          </IconButton>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex items-center gap-2 border-t border-rule px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}

/* ----------------------------------------------------------------- modal */

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  width = 'max-w-[520px]',
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: ReactNode
  footer?: ReactNode
  width?: string
  children?: ReactNode
}) {
  const panelRef = useOverlayBehavior(open, onClose)
  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-6 pt-[12vh]">
      <Scrim onClose={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative w-full rounded-[10px] bg-surface shadow-modal',
          width,
        )}
      >
        <header className="px-5 pb-3 pt-4">
          <h2 className="text-[18px] leading-7">{title}</h2>
          {description && (
            <p className="mt-1 text-[13px] leading-5 text-ink-muted">{description}</p>
          )}
        </header>
        {children && <div className="px-5 pb-1">{children}</div>}
        {footer && (
          <footer className="mt-4 flex items-center justify-end gap-2 border-t border-rule px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}

/* --------------------------------------------------------- confirmation */

/**
 * Destructive confirmation. The title must name the object being destroyed —
 * "Delete 3 programs?" not "Are you sure?" (docs/00-overview.md §6.5).
 * Deliberately no type-to-confirm: a red button plus a name is enough.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  destructive = false,
  busy = false,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  description?: ReactNode
  confirmLabel?: string
  destructive?: boolean
  busy?: boolean
}) {
  const [working, setWorking] = useState(false)

  const confirm = useCallback(async () => {
    setWorking(true)
    try {
      await onConfirm()
    } finally {
      setWorking(false)
    }
  }, [onConfirm])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      width="max-w-[460px]"
      footer={
        <>
          <Button size="sm" onClick={onClose} disabled={busy || working}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={destructive ? 'danger' : 'primary'}
            loading={busy || working}
            onClick={confirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  )
}
